import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { validateWorkEmail, countWords } from '../../utils/validation';
import apiService from '../../services/api';
import { trackLinkedInConversion, LINKEDIN_CONVERSIONS } from '../../services/linkedin';
import './RequestDemoPage.css';

const COMPANY_SIZES = [
  '1–50', '51–200', '201–1,000', '1,001–5,000', '5,001–20,000', '20,000+',
];

const INDUSTRIES = [
  'Healthcare', 'Insurance', 'Financial Services', 'Banking',
  'Logistics / Supply Chain', 'Life Sciences', 'Manufacturing',
  'Retail', 'Technology', 'Government / Public Sector', 'Other',
];

const TIMELINES = [
  'Immediately', 'Within 30 days', 'Within 90 days',
  'This quarter', 'Exploring / future planning',
];

const INTEREST_OPTIONS = [
  'Product demo', 'Beta program', 'Pilot discussion',
  'Enterprise deployment', 'Strategic partnership',
];

const EXPECTED_SCALES = [
  'Single team workflow', 'Multi-team workflow',
  'Department-wide', 'Enterprise-wide', 'Not sure yet',
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function RequestDemoPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [useCaseWordCount, setUseCaseWordCount] = useState(0);
  const [currentStateWordCount, setCurrentStateWordCount] = useState(0);

  const {
    register, handleSubmit, watch, setValue,
    formState: { errors },
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      first_name: '', last_name: '', work_email: '', phone: '',
      company_name: '', job_title: '', company_website: '',
      company_size: '', industry: '', primary_use_case: '',
      current_state_notes: '', timeline: '', interest_type: [],
      team_involved: '', expected_scale: '', consent_given: false,
      website_url_confirm: '',
    },
  });

  const watchUseCase = watch('primary_use_case');
  const watchCurrentState = watch('current_state_notes');

  useEffect(() => { setUseCaseWordCount(countWords(watchUseCase)); }, [watchUseCase]);
  useEffect(() => { setCurrentStateWordCount(countWords(watchCurrentState)); }, [watchCurrentState]);

  const onSubmit = useCallback(async (formData) => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        utm_source: searchParams.get('utm_source') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
        utm_term: searchParams.get('utm_term') || undefined,
        utm_content: searchParams.get('utm_content') || undefined,
      };
      await apiService.submitDemoRequest(payload);
      trackLinkedInConversion(LINKEDIN_CONVERSIONS.DEMO_REQUEST);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = err?.response?.data?.error || 'Something went wrong. Please try again.';
      setSubmitError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }, [searchParams]);

  if (submitted) {
    return (
      <div className="rdp">
        <nav className="rdp-nav">
          <div className="rdp-nav__inner">
            <a href="/" className="rdp-nav__logo" aria-label="CerebralMachines home">
              <img src="/CMLogo.png" alt="CerebralMachines" />
            </a>
          </div>
        </nav>
        <section className="rdp-success">
          <motion.div className="rdp-success__card" initial="hidden" animate="visible" variants={fadeUp}>
            <div className="rdp-success__icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#22D3EE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h1 className="rdp-success__title">Request Received</h1>
            <p className="rdp-success__body">
              Thank you for your interest in CerebralMachines. Our team reviews each request
              carefully and will reach out if there is a fit.
            </p>
            <button className="rdp-btn--primary" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </motion.div>
        </section>
      </div>
    );
  }

  return (
    <div className="rdp">
      <nav className="rdp-nav">
        <div className="rdp-nav__inner">
          <a href="/" className="rdp-nav__logo" aria-label="CerebralMachines home">
            <img src="/CMLogo.png" alt="CerebralMachines" />
          </a>
          <div className="rdp-nav__actions">
            <button className="rdp-btn--ghost" onClick={() => navigate('/login')}>Sign In</button>
          </div>
        </div>
      </nav>

      <section className="rdp-hero">
        <div className="rdp-hero__bg-glow" />
        <div className="rdp-container">
          <motion.div className="rdp-hero__content" initial="hidden" animate="visible" variants={fadeUp}>
            <h1 className="rdp-hero__title">Request a Demo</h1>
            <p className="rdp-hero__subtitle">
              Tell us about your organization and the business process you want to improve.
              We review each request carefully to ensure alignment.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="rdp-form-section">
        <div className="rdp-container rdp-container--narrow">
          {submitError && (
            <motion.div className="rdp-error-banner" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              {submitError}
            </motion.div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {/* Honeypot */}
            <div style={{ position: 'absolute', left: '-9999px', opacity: 0 }} aria-hidden="true">
              <input tabIndex={-1} autoComplete="off" {...register('website_url_confirm')} />
            </div>

            {/* ─── Section 1: Contact Information ─── */}
            <motion.div className="rdp-card" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={0}>
              <h2 className="rdp-card__title">Contact Information</h2>
              <div className="rdp-grid rdp-grid--2">
                <div className="rdp-field">
                  <label className="rdp-label">First Name <span className="rdp-req">*</span></label>
                  <input className={`rdp-input ${errors.first_name ? 'rdp-input--error' : ''}`}
                    placeholder="First name" maxLength={80}
                    {...register('first_name', { required: 'First name is required', maxLength: { value: 80, message: 'Max 80 characters' } })}
                  />
                  {errors.first_name && <span className="rdp-field-error">{errors.first_name.message}</span>}
                </div>
                <div className="rdp-field">
                  <label className="rdp-label">Last Name <span className="rdp-req">*</span></label>
                  <input className={`rdp-input ${errors.last_name ? 'rdp-input--error' : ''}`}
                    placeholder="Last name" maxLength={80}
                    {...register('last_name', { required: 'Last name is required', maxLength: { value: 80, message: 'Max 80 characters' } })}
                  />
                  {errors.last_name && <span className="rdp-field-error">{errors.last_name.message}</span>}
                </div>
              </div>

              <div className="rdp-grid rdp-grid--2">
                <div className="rdp-field">
                  <label className="rdp-label">Work Email <span className="rdp-req">*</span></label>
                  <input className={`rdp-input ${errors.work_email ? 'rdp-input--error' : ''}`}
                    type="email" placeholder="name@company.com" maxLength={255}
                    {...register('work_email', {
                      required: 'Work email is required',
                      validate: (v) => {
                        const r = validateWorkEmail(v);
                        return r.isValid || r.message;
                      },
                    })}
                  />
                  {errors.work_email && <span className="rdp-field-error">{errors.work_email.message}</span>}
                </div>
                <div className="rdp-field">
                  <label className="rdp-label">Phone Number <span className="rdp-req">*</span></label>
                  <input className={`rdp-input ${errors.phone ? 'rdp-input--error' : ''}`}
                    type="tel" placeholder="(555) 555-5555" maxLength={30}
                    {...register('phone', {
                      required: 'Phone number is required',
                      validate: (v) => {
                        const digits = v.replace(/[^\d]/g, '');
                        return digits.length >= 10 || 'Please enter a valid business phone number.';
                      },
                    })}
                  />
                  {errors.phone && <span className="rdp-field-error">{errors.phone.message}</span>}
                </div>
              </div>

              <div className="rdp-grid rdp-grid--2">
                <div className="rdp-field">
                  <label className="rdp-label">Company Name <span className="rdp-req">*</span></label>
                  <input className={`rdp-input ${errors.company_name ? 'rdp-input--error' : ''}`}
                    placeholder="Company name" maxLength={150}
                    {...register('company_name', { required: 'Company name is required', maxLength: { value: 150, message: 'Max 150 characters' } })}
                  />
                  {errors.company_name && <span className="rdp-field-error">{errors.company_name.message}</span>}
                </div>
                <div className="rdp-field">
                  <label className="rdp-label">Job Title <span className="rdp-req">*</span></label>
                  <input className={`rdp-input ${errors.job_title ? 'rdp-input--error' : ''}`}
                    placeholder="Your role" maxLength={120}
                    {...register('job_title', { required: 'Job title is required', maxLength: { value: 120, message: 'Max 120 characters' } })}
                  />
                  {errors.job_title && <span className="rdp-field-error">{errors.job_title.message}</span>}
                </div>
              </div>
            </motion.div>

            {/* ─── Section 2: Company Details ─── */}
            <motion.div className="rdp-card" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={1}>
              <h2 className="rdp-card__title">Company Details</h2>
              <div className="rdp-field">
                <label className="rdp-label">Company Website</label>
                <input className="rdp-input" placeholder="https://company.com" maxLength={500}
                  {...register('company_website')}
                />
              </div>

              <div className="rdp-grid rdp-grid--2">
                <div className="rdp-field">
                  <label className="rdp-label">Company Size <span className="rdp-req">*</span></label>
                  <select className={`rdp-select ${errors.company_size ? 'rdp-input--error' : ''}`}
                    {...register('company_size', { required: 'Company size is required' })}
                  >
                    <option value="">Select company size</option>
                    {COMPANY_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {errors.company_size && <span className="rdp-field-error">{errors.company_size.message}</span>}
                </div>
                <div className="rdp-field">
                  <label className="rdp-label">Industry <span className="rdp-req">*</span></label>
                  <select className={`rdp-select ${errors.industry ? 'rdp-input--error' : ''}`}
                    {...register('industry', { required: 'Industry is required' })}
                  >
                    <option value="">Select industry</option>
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                  {errors.industry && <span className="rdp-field-error">{errors.industry.message}</span>}
                </div>
              </div>
            </motion.div>

            {/* ─── Section 3: Business Need ─── */}
            <motion.div className="rdp-card" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={2}>
              <h2 className="rdp-card__title">Business Need</h2>

              <div className="rdp-field">
                <label className="rdp-label">
                  Primary Use Case You Want to Solve <span className="rdp-req">*</span>
                </label>
                <textarea className={`rdp-textarea ${errors.primary_use_case ? 'rdp-input--error' : ''}`}
                  rows={6}
                  placeholder="Describe the specific workflow, decision process, or operational problem you want to improve."
                  {...register('primary_use_case', {
                    required: 'Primary use case is required',
                    validate: (v) => {
                      const wc = countWords(v);
                      if (wc > 400) return 'Please describe your primary use case in 400 words or fewer.';
                      return true;
                    },
                  })}
                />
                <div className="rdp-field-meta">
                  <span className={`rdp-word-count ${useCaseWordCount > 400 ? 'rdp-word-count--over' : ''}`}>
                    {useCaseWordCount} / 400 words
                  </span>
                  {errors.primary_use_case && <span className="rdp-field-error">{errors.primary_use_case.message}</span>}
                </div>
                <span className="rdp-hint">Maximum 400 words.</span>
              </div>

              <div className="rdp-field">
                <label className="rdp-label">Current State / Existing AI or Automation Efforts</label>
                <textarea className="rdp-textarea" rows={4}
                  placeholder="What have you already tried, and where is it falling short?"
                  {...register('current_state_notes', {
                    validate: (v) => {
                      if (v && countWords(v) > 250) return 'Please keep to 250 words or fewer.';
                      return true;
                    },
                  })}
                />
                <div className="rdp-field-meta">
                  <span className={`rdp-word-count ${currentStateWordCount > 250 ? 'rdp-word-count--over' : ''}`}>
                    {currentStateWordCount} / 250 words
                  </span>
                  {errors.current_state_notes && <span className="rdp-field-error">{errors.current_state_notes.message}</span>}
                </div>
              </div>

              <div className="rdp-field">
                <label className="rdp-label">Timeline <span className="rdp-req">*</span></label>
                <select className={`rdp-select ${errors.timeline ? 'rdp-input--error' : ''}`}
                  {...register('timeline', { required: 'Timeline is required' })}
                >
                  <option value="">Select timeline</option>
                  {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                {errors.timeline && <span className="rdp-field-error">{errors.timeline.message}</span>}
              </div>

              <div className="rdp-field">
                <label className="rdp-label">Interest Type <span className="rdp-req">*</span></label>
                <div className="rdp-checkbox-group">
                  {INTEREST_OPTIONS.map((opt) => (
                    <label key={opt} className="rdp-checkbox-label">
                      <input type="checkbox" value={opt}
                        {...register('interest_type', {
                          validate: (v) => (v && v.length > 0) || 'Select at least one interest type',
                        })}
                      />
                      <span className="rdp-checkbox-text">{opt}</span>
                    </label>
                  ))}
                </div>
                {errors.interest_type && <span className="rdp-field-error">{errors.interest_type.message}</span>}
              </div>
            </motion.div>

            {/* ─── Section 4: Qualification (Optional) ─── */}
            <motion.div className="rdp-card" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={3}>
              <h2 className="rdp-card__title">Additional Context <span className="rdp-optional">(Optional)</span></h2>
              <div className="rdp-grid rdp-grid--2">
                <div className="rdp-field">
                  <label className="rdp-label">Estimated Team or Function Involved</label>
                  <input className="rdp-input"
                    placeholder="e.g. Claims operations, compliance, architecture"
                    maxLength={300}
                    {...register('team_involved')}
                  />
                </div>
                <div className="rdp-field">
                  <label className="rdp-label">Expected Volume / Scale</label>
                  <select className="rdp-select" {...register('expected_scale')}>
                    <option value="">Select scale</option>
                    {EXPECTED_SCALES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </motion.div>

            {/* ─── Consent & Submit ─── */}
            <motion.div className="rdp-card rdp-card--submit" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} custom={4}>
              <div className="rdp-field">
                <label className={`rdp-checkbox-label rdp-consent ${errors.consent_given ? 'rdp-consent--error' : ''}`}>
                  <input type="checkbox"
                    {...register('consent_given', { required: 'You must agree to be contacted' })}
                  />
                  <span className="rdp-checkbox-text">
                    I agree to be contacted by CerebralMachines regarding my request.
                  </span>
                </label>
                {errors.consent_given && <span className="rdp-field-error">{errors.consent_given.message}</span>}
              </div>
              <button type="submit" className="rdp-btn--submit" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </motion.div>
          </form>
        </div>
      </section>

      <footer className="rdp-footer">
        <p>&copy; 2026 CerebralMachines. All rights reserved.</p>
      </footer>
    </div>
  );
}
